import {
  type InfiniteData,
  keepPreviousData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useState } from "react";
import {
  addAssetToLibraryApiAssetsAssetIdLibraryPostMutation,
  listCatalogApiAssetsCatalogGetInfiniteOptions,
  listCatalogApiAssetsCatalogGetInfiniteQueryKey,
  listCatalogCategoriesApiAssetsCatalogCategoriesGetOptions,
  listCatalogManufacturersApiAssetsCatalogManufacturersGetOptions,
  listCatalogSubcategoriesApiAssetsCatalogSubcategoriesGetOptions,
  listProjectAssetsApiProjectsProjectIdAssetsGetQueryKey,
} from "../generated/api/@tanstack/react-query.gen";
import {
  addAssetToLibraryApiAssetsAssetIdLibraryPost,
  attachProjectAssetApiProjectsProjectIdAssetsPost,
} from "../generated/api/sdk.gen";
import type {
  CatalogAssetPage,
  CatalogAssetResponse,
  CatalogCategoryResponse,
  CatalogManufacturer,
  CatalogSubcategoryResponse,
} from "../generated/api/types.gen";
import { invalidateAssets } from "../lib/queries";

/** 7 columns × 6 rows at the design width. */
const PAGE_SIZE = 42;

// No options: partial-matches the cached catalog for every manufacturer selection.
const catalogKey = listCatalogApiAssetsCatalogGetInfiniteQueryKey();

const saveKey = ["addAssetToLibrary"];

const SAVE_ERROR = "Couldn't save to your assets. Try again.";

export interface SourceCatalogState {
  assets: CatalogAssetResponse[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  hasMore: boolean;
  isFetchingMore: boolean;
  loadMore: () => void;
  saveToLibrary: (assetId: string) => void;
  /** Resolves true once the product is in the library and the project. */
  addToProject: (
    asset: CatalogAssetResponse,
    project: { id: string; name: string },
  ) => Promise<boolean>;
  saveError: string | null;
  dismissSaveError: () => void;
}

/** Set one catalog item's ``in_library`` flag in every cached catalog view. */
function setInLibrary(
  queryClient: QueryClient,
  assetId: string,
  inLibrary: boolean,
) {
  queryClient.setQueriesData<InfiniteData<CatalogAssetPage>>(
    { queryKey: catalogKey },
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map((asset) =>
            asset.id === assetId ? { ...asset, in_library: inLibrary } : asset,
          ),
        })),
      },
  );
}

/**
 * Saving a catalog item into the user's library. The item is marked
 * ``in_library`` optimistically, so its button turns Saved on click and can't
 * send a second save; a failure rolls that back and reports it. The catalog
 * and the library list that My Assets and the asset pickers read are then
 * refetched.
 */
function useSaveMutation(reportError: (message: string) => void) {
  const queryClient = useQueryClient();
  return useMutation({
    ...addAssetToLibraryApiAssetsAssetIdLibraryPostMutation(),
    mutationKey: saveKey,
    onMutate: async ({ path }) => {
      // An in-flight refetch landing after this would overwrite the flag.
      await queryClient.cancelQueries({ queryKey: catalogKey });
      setInLibrary(queryClient, path.asset_id, true);
    },
    onError: (_error, { path }) => {
      setInLibrary(queryClient, path.asset_id, false);
      reportError(SAVE_ERROR);
    },
    onSettled: () => {
      // A refetch while another save is in flight would report that item as
      // unsaved and undo its optimistic flag, so only the last one refetches.
      if (queryClient.isMutating({ mutationKey: saveKey }) > 1) return;
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: catalogKey }),
        invalidateAssets(queryClient),
      ]);
    },
  });
}

/**
 * Filing a product in a project from the details modal: it is saved to the
 * library, then attached. Both calls are idempotent, so a retry after a
 * partial failure is safe. Each failure is reported once.
 *
 * The save runs even when ``in_library`` is set, since that flag may be an
 * optimistic one for a save that is still in flight and may fail. It skips
 * the save mutation so the attach doesn't wait on that mutation's catalog
 * refetch; the flag is set directly instead.
 */
function useAddToProject(
  reportError: (message: string) => void,
): SourceCatalogState["addToProject"] {
  const queryClient = useQueryClient();

  return async (asset, project) => {
    try {
      await addAssetToLibraryApiAssetsAssetIdLibraryPost({
        path: { asset_id: asset.id },
      });
    } catch {
      reportError(SAVE_ERROR);
      return false;
    }
    setInLibrary(queryClient, asset.id, true);
    try {
      await attachProjectAssetApiProjectsProjectIdAssetsPost({
        path: { project_id: project.id },
        body: { asset_id: asset.id },
      });
    } catch {
      reportError(`Couldn't add to ${project.name}. Try again.`);
      return false;
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: listProjectAssetsApiProjectsProjectIdAssetsGetQueryKey({
          path: { project_id: project.id },
        }),
      }),
      invalidateAssets(queryClient),
    ]);
    return true;
  };
}

/** Saving to the library and filing in a project, sharing one error toast. */
function useCatalogSaving() {
  const [saveError, setSaveError] = useState<string | null>(null);
  const dismissSaveError = useCallback(() => setSaveError(null), []);
  const save = useSaveMutation(setSaveError);

  return {
    saveToLibrary: (assetId: string) =>
      save.mutate({ path: { asset_id: assetId } }),
    addToProject: useAddToProject(setSaveError),
    saveError,
    dismissSaveError,
  };
}

export interface CatalogFilterSelection {
  manufacturers?: string[];
  categoryIds?: string[];
  subcategoryIds?: string[];
  query?: string;
}

/** Query params for a filter selection; an empty one is left out. */
const manufacturerQuery = (manufacturers: string[] = []) =>
  manufacturers.length ? { manufacturer: manufacturers } : {};
const categoryQuery = (categoryIds: string[] = []) =>
  categoryIds.length ? { category_id: categoryIds } : {};
const subcategoryQuery = (subcategoryIds: string[] = []) =>
  subcategoryIds.length ? { subcategory_id: subcategoryIds } : {};
const searchQuery = (query?: string) => (query ? { q: query } : {});

/**
 * The manufacturer catalog, narrowed to any of the selected manufacturers and
 * any of the selected categories (the two combine), each category narrowed by
 * its own selected subcategories, and to what the search ``query`` finds, best
 * fit first, and paged by offset, plus saving an item into the user's library.
 */
export function useSourceCatalog({
  manufacturers,
  categoryIds,
  subcategoryIds,
  query,
}: CatalogFilterSelection): SourceCatalogState {
  const catalog = useInfiniteQuery({
    ...listCatalogApiAssetsCatalogGetInfiniteOptions({
      query: {
        limit: PAGE_SIZE,
        ...manufacturerQuery(manufacturers),
        ...categoryQuery(categoryIds),
        ...subcategoryQuery(subcategoryIds),
        ...searchQuery(query),
      },
    }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.next_offset ?? undefined,
  });

  return {
    assets: catalog.data?.pages.flatMap((page) => page.items) ?? [],
    isLoading: catalog.isPending,
    isError: catalog.isError,
    refetch: () => {
      catalog.refetch();
    },
    hasMore: catalog.hasNextPage,
    isFetchingMore: catalog.isFetchingNextPage,
    loadMore: () => {
      catalog.fetchNextPage();
    },
    ...useCatalogSaving(),
  };
}

/** A list the filter drawer shows, with its loading and failure states. */
export interface CatalogListState<T> {
  list: T[];
  isLoading: boolean;
  /** A failed request, as opposed to an empty list. */
  isError: boolean;
  retry: () => void;
}

function listState<T>({
  data,
  isPending,
  isError,
  refetch,
}: {
  data: T[] | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => unknown;
}): CatalogListState<T> {
  return {
    list: data ?? [],
    isLoading: isPending,
    isError,
    retry: () => {
      refetch();
    },
  };
}

export type CatalogManufacturersState = CatalogListState<CatalogManufacturer>;

/**
 * Every catalog manufacturer with its product count in the selected
 * categories (each narrowed by its own selected subcategories) that the search
 * ``query`` finds, for the filter drawer. The previous counts stay up while new ones load, so the list doesn't
 * blank out on every category toggle.
 */
export function useCatalogManufacturers(
  categoryIds: string[],
  query?: string,
  subcategoryIds: string[] = [],
): CatalogManufacturersState {
  return listState(
    useQuery({
      ...listCatalogManufacturersApiAssetsCatalogManufacturersGetOptions({
        query: {
          ...categoryQuery(categoryIds),
          ...subcategoryQuery(subcategoryIds),
          ...searchQuery(query),
        },
      }),
      placeholderData: keepPreviousData,
    }),
  );
}

export type CatalogCategoriesState = CatalogListState<CatalogCategoryResponse>;

/**
 * The catalog categories, most populated first, with their product counts
 * from the selected manufacturers that the search ``query`` finds, for the
 * filter drawer. Like ``useCatalogManufacturers``, the previous counts stay up
 * while new ones load.
 */
export function useCatalogCategories(
  manufacturers: string[],
  query?: string,
): CatalogCategoriesState {
  return listState(
    useQuery({
      ...listCatalogCategoriesApiAssetsCatalogCategoriesGetOptions({
        query: { ...manufacturerQuery(manufacturers), ...searchQuery(query) },
      }),
      placeholderData: keepPreviousData,
    }),
  );
}

export type CatalogSubcategoriesState =
  CatalogListState<CatalogSubcategoryResponse>;

/**
 * Subcategories of the selected categories, most populated first, with counts
 * narrowed by manufacturers and search ``q`` (ignoring the subcategory
 * selection itself). Returns an empty list without a category selection —
 * subcategories only show after a category is selected.
 */
export function useCatalogSubcategories(
  categoryIds: string[],
  manufacturers: string[],
  query?: string,
): CatalogSubcategoriesState {
  const result = useQuery({
    ...listCatalogSubcategoriesApiAssetsCatalogSubcategoriesGetOptions({
      query: {
        ...categoryQuery(categoryIds),
        ...manufacturerQuery(manufacturers),
        ...searchQuery(query),
      },
    }),
    placeholderData: keepPreviousData,
    enabled: categoryIds.length > 0,
  });
  return listState({
    ...result,
    // Drop placeholder rows from categories that were just unchecked while the
    // next request is still in flight.
    data: result.data?.filter((sub) =>
      categoryIds.includes(sub.catalog_category_id),
    ),
  });
}
